import type { Role } from "../types";
import { classifyLocation, domainConfig, joinUrlPath, runtimeUrls, tenantUrl, validateReturnTo, type RuntimeLocation } from "../config/domains";

export interface WorkspaceAccess {
  organizationId: string;
  name: string;
  slug: string;
  role: Role;
  status: "INVITED" | "ACTIVE" | "DISABLED";
  organizationActive: boolean;
}

export interface AccessContext {
  isPositronicoOwner: boolean;
  ownerAuthorized?: boolean;
  ownerMfaRequired?: boolean;
  aal?: "aal1" | "aal2";
  workspaces: WorkspaceAccess[];
}

export interface TenantAccess {
  exists: boolean;
  organizationActive: boolean;
  membershipStatus: "INVITED" | "ACTIVE" | "DISABLED" | null;
  role: Role | null;
  allowed: boolean;
}

export const emptyAccessContext: AccessContext = { isPositronicoOwner: false, ownerAuthorized: false, ownerMfaRequired: false, aal: "aal1", workspaces: [] };

export type TenantAccessOutcome = "allowed" | "not-found" | "organization-inactive" | "membership-invited" | "membership-disabled" | "forbidden";

export function needsPasswordSetup(access: AccessContext) {
  return !access.isPositronicoOwner
    && access.workspaces.some((workspace) => workspace.status === "INVITED" && workspace.organizationActive)
    && !access.workspaces.some((workspace) => workspace.status === "ACTIVE" && workspace.organizationActive);
}

export function tenantAccessOutcome(access: TenantAccess): TenantAccessOutcome {
  if (!access.exists) return "not-found";
  if (!access.organizationActive) return "organization-inactive";
  if (access.membershipStatus === "INVITED") return "membership-invited";
  if (access.membershipStatus === "DISABLED") return "membership-disabled";
  return access.allowed ? "allowed" : "forbidden";
}

export function canAccessGlobalAdmin(access: AccessContext) { return access.isPositronicoOwner && access.ownerAuthorized !== false && access.aal !== "aal1"; }

export function postLoginDestination(access: AccessContext, requestedReturnTo?: string | null, location: RuntimeLocation = window.location, stagingOrigin = domainConfig.stagingOrigin) {
  const urls = runtimeUrls(location, stagingOrigin);
  if (needsPasswordSetup(access)) return joinUrlPath(urls.appUrl, "/set-password");
  const active = access.workspaces.filter((workspace) => workspace.status === "ACTIVE" && workspace.organizationActive);
  if (requestedReturnTo) {
    const safe = validateReturnTo(requestedReturnTo, location, stagingOrigin);
    const target = new URL(safe);
    const targetSurface = classifyLocation(target, undefined, stagingOrigin);
    const matching = targetSurface.kind === "tenant" ? active.find((workspace) => workspace.slug === targetSurface.slug) : undefined;
    if (targetSurface.kind === "app" || access.isPositronicoOwner || matching) return safe;
  }
  if (access.isPositronicoOwner && access.ownerAuthorized === false) return joinUrlPath(urls.appUrl, "/mfa");
  if (access.isPositronicoOwner) return joinUrlPath(urls.appUrl, "/admin");
  if (active.length === 1) return tenantUrl(active[0]!.slug, "/", location, stagingOrigin);
  if (active.length > 1) return joinUrlPath(urls.appUrl, "/workspaces");
  return joinUrlPath(urls.appUrl, "/workspaces?state=no-access");
}

export function authCallbackDestination(type: string, access: AccessContext, requestedReturnTo?: string | null, location: RuntimeLocation = window.location, stagingOrigin = domainConfig.stagingOrigin) {
  const urls = runtimeUrls(location, stagingOrigin);
  if (type === "invite") return joinUrlPath(urls.appUrl, "/set-password");
  if (type === "recovery") return joinUrlPath(urls.appUrl, "/reset-password");
  return postLoginDestination(access, requestedReturnTo, location, stagingOrigin);
}

export function normalizeAccessContext(value: unknown): AccessContext {
  if (!value || typeof value !== "object") return emptyAccessContext;
  const source = value as Record<string, unknown>;
  const rows = Array.isArray(source.workspaces) ? source.workspaces : [];
  const workspaces = rows.flatMap((row): WorkspaceAccess[] => {
    if (!row || typeof row !== "object") return [];
    const item = row as Record<string, unknown>;
    const role = item.role;
    const status = item.status;
    if (typeof item.organization_id !== "string" || typeof item.name !== "string" || typeof item.slug !== "string") return [];
    if (role !== "POSITRONICO_OWNER" && role !== "CLIENT_ADMIN" && role !== "CLIENT_MEMBER") return [];
    if (status !== "INVITED" && status !== "ACTIVE" && status !== "DISABLED") return [];
    return [{ organizationId: item.organization_id, name: item.name, slug: item.slug, role, status, organizationActive: item.organization_active !== false }];
  });
  const aal = source.aal === "aal2" ? "aal2" : "aal1";
  return {
    isPositronicoOwner: source.is_positronico_owner === true,
    ownerAuthorized: source.owner_authorized === true,
    ownerMfaRequired: source.owner_mfa_required === true,
    aal,
    workspaces,
  };
}
